import {
  type CommandFailure,
  type CommandReceipt,
  createGame,
  type EventJournal,
  type Game,
  planAdvance,
  type RecordState,
  type Snapshot,
} from "@e308/core";
import { admitEnvelope, drainBoundary, nextControllerSequence } from "./authority.js";
import { type ConflictContent, createConflictContent } from "./definition.js";
import type { Micro } from "./exact-numbers.js";
import { OVERTIME_LIMIT_MS } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import {
  matchControllerGeneration,
  promoteMatchAuthority,
  recordMatchPause,
  transitionMatchController,
} from "./match-control.js";
import {
  decodeContainer,
  decodeSaveArtifact,
  encodeReplay,
  encodeSave,
  type MatchTimelineEntry,
} from "./match-persistence.js";
import { observeMatch, readMatchEvents } from "./match-projection.js";
import { decodeControllerEnvelope } from "./protocol.js";
import type {
  AcceptedIntent,
  AuthorityState,
  CanonicalPhase,
  ConflictConfig,
  ConflictEvent,
  ControllerEnvelope,
  SeatObservation,
  SubmitResult,
} from "./types.js";

export interface TickResult {
  readonly commands: readonly {
    readonly seatId: string;
    readonly sequence: number;
    readonly result:
      | { readonly ok: true; readonly value: CommandReceipt }
      | { readonly ok: false; readonly error: CommandFailure<Micro> };
  }[];
  readonly advanced: boolean;
  readonly events: readonly ConflictEvent[];
  readonly phases: readonly CanonicalPhase[];
}

interface RestoredMatch {
  readonly snapshot: Snapshot<Micro>;
  readonly timeline: readonly MatchTimelineEntry[];
}
export class ConflictMatch {
  readonly #content: ConflictContent;
  readonly #game: Game<Micro>;
  readonly #seatOrder: ReadonlyMap<string, number>;
  readonly #config: ConflictConfig;
  readonly #timeline: MatchTimelineEntry[];
  constructor(config: ConflictConfig, restored?: RestoredMatch) {
    this.#content = createConflictContent(config);
    this.#game = createGame(
      this.#content.definition,
      restored ? { snapshot: restored.snapshot } : undefined,
    );
    this.#seatOrder = new Map(this.#content.seats.map((seat, index) => [seat.seat.id, index]));
    this.#config = structuredClone(config);
    this.#timeline = restored ? [...structuredClone(restored.timeline)] : [];
  }
  static restore(raw: string): ConflictMatch {
    const saved = decodeSaveArtifact(raw);
    return new ConflictMatch(saved.config, saved);
  }
  static restoreAs(raw: string, matchId: string): ConflictMatch {
    const saved = decodeSaveArtifact(raw);
    return new ConflictMatch({ ...saved.config, matchId }, saved);
  }
  static replay(raw: string): ConflictMatch {
    const replay = decodeContainer(raw, "kilnfront-replay");
    const match = new ConflictMatch(replay.config);
    for (const entry of replay.timeline) {
      if (entry.kind === "tick") match.tick(entry.elapsedMs);
      else if (entry.kind === "controller-transition") {
        requireReplayTime(match, entry.seatId, entry.atGameMs);
        match.transitionController(entry.seatId, entry.sessionId, entry.generation, entry.mode);
      } else if (entry.kind === "pause-transition") {
        requireReplayTime(match, replay.config.seats[0]?.id ?? "", entry.atGameMs);
        match.recordPauseTransition(entry.paused);
      } else {
        if (match.observeAs(entry.authenticatedSeatId).gameTimeMs !== entry.atGameMs)
          throw new TypeError("Replay input is not at its recorded boundary");
        const result = match.submitAs(entry.authenticatedSeatId, entry.envelope);
        if (!result.ok || result.duplicate) throw new TypeError("Replay input was not accepted");
      }
    }
    return match;
  }
  submitAs(authenticatedSeatId: string, input: unknown): SubmitResult {
    try {
      if (this.lifecycleState().status === "ended")
        return rejection("match-ended", "The match has ended");
      const envelope = decodeControllerEnvelope(input);
      this.validateEnvelopeAuthority(authenticatedSeatId, envelope);
      this.#content.commandFor(envelope.seatId, envelope.intent);
      const boundary = this.#game.getSnapshot().gameTimeMs + this.#content.stepMs;
      const preview = admitEnvelope(this.authorityState(), envelope, boundary);
      if (preview.duplicate) return { ok: true, receipt: preview.receipt, duplicate: true };
      let accepted = preview;
      const result = this.#game.dispatch({
        id: `admit:${envelope.requestId}`,
        execute: (transaction) => {
          accepted = admitEnvelope(
            transaction.getRecord(this.#content.authority),
            envelope,
            boundary,
          );
          transaction.setRecord(this.#content.authority, accepted.state);
        },
      });
      /* v8 ignore next -- validated admission uses a total record transaction */
      if (!result.ok) return rejection(result.error.code, "Admission transaction failed");
      this.#timeline.push({
        kind: "accepted-input",
        atGameMs: this.#game.getSnapshot().gameTimeMs,
        authenticatedSeatId,
        envelope,
      });
      return { ok: true, receipt: accepted.receipt, duplicate: false };
    } catch (error) {
      return rejection(
        "invalid-envelope",
        error instanceof Error ? error.message : "Invalid order",
      );
    }
  }
  tick(elapsedMs: number): TickResult {
    const snapshot = this.#game.getSnapshot();
    planAdvance(snapshot, elapsedMs, this.#content.stepMs);
    const maximumElapsed = Math.max(
      0,
      this.lifecycleState().timeLimitMs +
        OVERTIME_LIMIT_MS -
        snapshot.gameTimeMs -
        snapshot.remainderMs,
    );
    const boundedElapsed = Math.min(elapsedMs, maximumElapsed);
    const plan = planAdvance(snapshot, boundedElapsed, this.#content.stepMs);
    const journal = snapshot.domainEventJournal as EventJournal;
    const beforeSequence = journal.nextSequence - 1n;
    const phases: CanonicalPhase[] = [];
    let intents: readonly AcceptedIntent[] = [];
    if (this.lifecycleState().status === "ended")
      return Object.freeze({
        commands: Object.freeze([]),
        advanced: false,
        events: Object.freeze([]),
        phases: Object.freeze([]),
      });
    if (plan.steps > 0) {
      intents = this.drainNextBoundary();
    }
    const commands = intents.map((entry) => ({
      seatId: entry.seatId,
      sequence: entry.controllerSequence,
      result: this.#game.dispatch(this.#content.commandFor(entry.seatId, entry.intent)),
    }));
    const beforeRevision = snapshot.revision;
    const advance = (amount: number) => {
      const result = this.#game.advance(amount, (transaction, stepSeconds) => {
        phases.push("apply-intents");
        this.#content.runStep(transaction, stepSeconds * 1_000, (phase) => phases.push(phase));
      });
      if (!result.ok) throw new Error(`Simulation advance failed: ${result.error.code}`);
    };
    if (plan.steps === 0) advance(boundedElapsed);
    else {
      let remaining = boundedElapsed;
      const first = this.#content.stepMs - snapshot.remainderMs;
      advance(first);
      remaining -= first;
      while (remaining >= this.#content.stepMs && this.lifecycleState().status !== "ended") {
        advance(this.#content.stepMs);
        remaining -= this.#content.stepMs;
      }
      if (remaining > 0 && this.lifecycleState().status !== "ended") advance(remaining);
    }
    const result = Object.freeze({
      commands: Object.freeze(commands),
      advanced: this.#game.getSnapshot().revision !== beforeRevision,
      events: this.readEvents(beforeSequence),
      phases: Object.freeze(phases),
    });
    this.#timeline.push({ kind: "tick", elapsedMs });
    return result;
  }

  exportSave(wallAnchorMs = 0): string {
    return encodeSave(
      this.#content.definition,
      this.#game.getSnapshot(),
      this.#config,
      this.#timeline,
      wallAnchorMs,
    );
  }
  exportRecoverySave(retainGameMs = 10_000): string {
    if (!Number.isSafeInteger(retainGameMs) || retainGameMs < 0)
      throw new TypeError("Invalid recovery input retention duration");
    const now = this.#game.getSnapshot().gameTimeMs;
    const retained = this.#timeline.filter(
      (entry) => entry.kind === "accepted-input" && entry.atGameMs >= now - retainGameMs,
    );
    return encodeSave(
      this.#content.definition,
      this.#game.getSnapshot(),
      this.#config,
      retained,
      0,
    );
  }
  exportReplay(): string {
    return encodeReplay(this.#config, this.#timeline);
  }

  authorityEpoch(): number {
    return this.authorityState().authorityEpoch;
  }

  promoteAuthority(authorityEpoch: number): void {
    promoteMatchAuthority(this.#game, this.#content, authorityEpoch);
  }

  transitionController(
    seatId: string,
    sessionId: string,
    generation: number,
    mode: "human" | "autopilot",
  ): void {
    transitionMatchController(
      this.#game,
      this.#content,
      this.#timeline,
      seatId,
      sessionId,
      generation,
      mode,
    );
  }

  recordPauseTransition(paused: boolean): void {
    recordMatchPause(this.#game, this.#content, this.#timeline, paused);
  }

  controllerGeneration(seatId: string): number {
    return matchControllerGeneration(this.#game, this.#content, seatId);
  }

  nextControllerSequence(seatId: string): number {
    if (!this.#content.seatById.has(seatId)) throw new TypeError(`Unknown seat: ${seatId}`);
    return nextControllerSequence(this.authorityState(), seatId);
  }
  forecastAs(seatId: string, elapsedMs: number): SeatObservation {
    const clone = new ConflictMatch(this.#config, {
      snapshot: this.#game.getSnapshot(),
      timeline: this.#timeline,
    });
    clone.tick(elapsedMs);
    return clone.observeAs(seatId);
  }

  observeAs(seatId: string): SeatObservation {
    return observeMatch(this.#game, this.#content, seatId);
  }

  readEvents(afterSequence = 0n): readonly ConflictEvent[] {
    return readMatchEvents(this.#game, afterSequence);
  }

  readEventsAs(seatId: string, afterSequence = 0n): readonly ConflictEvent[] {
    if (!this.#content.seatById.has(seatId)) throw new TypeError(`Unknown seat: ${seatId}`);
    return readMatchEvents(this.#game, afterSequence, seatId);
  }

  private authorityState(): AuthorityState {
    const state = this.#game.getSnapshot().records?.[this.#content.authority.id] as RecordState;
    return state.value as unknown as AuthorityState;
  }

  private lifecycleState(): MatchLifecycleState {
    const state = this.#game.getSnapshot().records?.[this.#content.lifecycle.id] as RecordState;
    return state.value as unknown as MatchLifecycleState;
  }

  private validateEnvelopeAuthority(
    authenticatedSeatId: string,
    envelope: ControllerEnvelope,
  ): void {
    if (envelope.matchId !== this.#content.matchId) throw new TypeError("Wrong match ID");
    if (envelope.seatId !== authenticatedSeatId) throw new TypeError("Forged seat identity");
    const model = this.#content.seatById.get(authenticatedSeatId);
    if (!model) throw new TypeError(`Unknown seat: ${authenticatedSeatId}`);
    const state = this.authorityState();
    if (envelope.authorityEpoch !== state.authorityEpoch)
      throw new TypeError("Stale authority epoch");
    if (envelope.controllerGeneration !== state.controllerGenerations[authenticatedSeatId]) {
      throw new TypeError("Stale controller generation");
    }
    const currentScope = this.#game.getSnapshot().scopeGenerations[model.scope.id] as bigint;
    if (BigInt(envelope.expectedScopeGeneration) !== currentScope) {
      throw new TypeError("Stale scope generation");
    }
  }

  private drainNextBoundary(): readonly AcceptedIntent[] {
    const boundary = this.#game.getSnapshot().gameTimeMs + this.#content.stepMs;
    let drained: readonly AcceptedIntent[] = [];
    const result = this.#game.dispatch({
      id: `drain:${boundary}`,
      execute: (transaction) => {
        const next = drainBoundary(
          transaction.getRecord(this.#content.authority),
          boundary,
          this.#seatOrder,
        );
        drained = next.intents;
        transaction.setRecord(this.#content.authority, next.state);
      },
    });
    /* v8 ignore next -- validated authority state makes boundary drain total */
    if (!result.ok) throw new Error("Could not drain admitted orders");
    return drained;
  }
}

function rejection(code: string, reason: string): SubmitResult {
  return Object.freeze({ ok: false, code, reason });
}

function requireReplayTime(match: ConflictMatch, seatId: string, expected: number): void {
  if (match.observeAs(seatId).gameTimeMs !== expected)
    throw new TypeError("Replay lifecycle transition is not at its recorded boundary");
}
